import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditAccountModal = ({ open, user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    role: '',
    provider_role: '',
    clinic_hours: '',
    phone: '',
    company_name: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  // Helper to get display name
  const getUserDisplayName = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.full_name || 'Unknown User';
  };

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        middle_name: user.middle_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        role: user.role || '',
        provider_role: user.provider_role || '',
        clinic_hours: user.clinic_hours || '',
        phone: user.phone || '',
        company_name: user.company_name || '',
        status: user.status || 'active'
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
        role: formData.role,
        status: formData.status
      };

      if (formData.role === 'provider') {
        updateData.provider_role = formData.provider_role;
        updateData.clinic_hours = formData.clinic_hours;
        updateData.phone = formData.phone;
      }

      if (formData.role === 'employer') {
        updateData.company_name = formData.company_name;
      }

      await axios.put(
        `${API}/admin/accounts/${user.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Account updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Account - {getUserDisplayName(user)}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Middle Name</Label>
              <Input
                value={formData.middle_name}
                onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Role</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                required
              >
                <option value="provider">Provider</option>
                <option value="admin">Admin</option>
                <option value="employer">Employer</option>
              </select>
            </div>
            <div>
              <Label>Account Status</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {formData.role === 'provider' && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Provider-specific fields
                </AlertDescription>
              </Alert>

              <div>
                <Label>Provider Role</Label>
                <Input
                  value={formData.provider_role}
                  onChange={(e) => setFormData({ ...formData, provider_role: e.target.value })}
                  placeholder="e.g., Primary Care Physician"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Clinic Hours</Label>
                  <Input
                    value={formData.clinic_hours}
                    onChange={(e) => setFormData({ ...formData, clinic_hours: e.target.value })}
                    placeholder="e.g., Mon-Fri, 9AM-5PM"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g., +1 (555) 123-4567"
                    className="mt-1"
                  />
                </div>
              </div>
            </>
          )}

          {formData.role === 'employer' && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Employer-specific fields
                </AlertDescription>
              </Alert>

              <div>
                <Label>Company / Business Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="e.g., Acme Corporation"
                  className="mt-1"
                  data-testid="edit-employer-company-name"
                />
              </div>
            </>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 btn-primary"
              disabled={saving}
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAccountModal;